import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import DashboardLayout from "../common/DashboardLayout";
import CertificationServices from "../../services/CertificationServices";
import { Link } from "react-router-dom";
import { certificateLinkFor, storedUserId } from "../../utils/certificateLink";

const statusLabels = {
  pending_review: "Pending Review",
  approved: "Approved",
  completed: "Completed",
  in_progress: "In Progress",
  not_started: "Not Started",
  expired: "Expired",
  rejected: "Rejected",
};

const normalizeCertifications = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.certifications)) return payload.certifications;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const formatDateDisplay = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const getCourseLabel = (cert) =>
  cert?.courseTitle ||
  cert?.title ||
  cert?.courseName ||
  cert?.courseKey ||
  "Untitled Course";

const getCertificateIdentifier = (cert = {}) =>
  cert._id || cert.id || `${cert.courseKey || "record"}-certificate`;

// Determine user role from localStorage
const getUserRole = () => {
  const role = localStorage.getItem("userrole");
  if (role === "1" || role === 1) return 1; // School Admin
  if (role === "2" || role === 2) return 2; // School Teacher
  if (role === "3" || role === 3) return 3; // Individual Teacher
  return 3; // Default to Individual Teacher
};

const CompletedCertifications = () => {
  // Only a fallback: rows carry their own owner. See utils/certificateLink.
  const fallbackUserId = storedUserId();
  const [certifications, setCertifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [userRole, setUserRole] = useState(3);
  const [userType, setUserType] = useState(null);

  useEffect(() => {
    // Get user role and type
    const role = getUserRole();
    setUserRole(role);
    
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    setUserType(user?.type || null);
  }, []);

  const fetchCertifications = async () => {
    setIsLoading(true);
    try {
      const response = await CertificationServices.getMyCertifications();
      const normalizedCerts = normalizeCertifications(response);
      setCertifications(normalizedCerts);
    } catch (error) {
      console.error("Failed to load certifications", error);
      Swal.fire(
        "Error",
        "We couldn't load your certifications. Please try again.",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCertifications();
    // Check and auto-assign certificates for historical completions
    const checkHistoricalCertificates = async () => {
      try {
        await CertificationServices.checkAndAssignHistoricalCertificates();
        // Refresh data after auto-assignment
        await fetchCertifications();
      } catch (error) {
        console.log("Historical certificate check completed or error:", error);
      }
    };
    checkHistoricalCertificates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownloadCertificate = async (cert, fallbackCourseName) => {
    if (!cert?.certificateUrl) {
      Swal.fire(
        "No certificate",
        "There is no certificate file saved for this course yet.",
        "info"
      );
      return;
    }

    const identifier = getCertificateIdentifier(cert);
    setDownloadingId(identifier);
    try {
      const safeCourseLabel = (fallbackCourseName || "Certificate").replace(
        /[\\/:*?"<>|]+/g,
        "_"
      );
      const { blob, filename, contentType } =
        await CertificationServices.downloadCertificate({
          certId:
            cert._id || cert.id || cert.certificationId || cert.courseKey,
          fallbackName: `${safeCourseLabel}.pdf`,
        });
      const blobUrl = window.URL.createObjectURL(
        new Blob([blob], { type: contentType })
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || `${safeCourseLabel}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to download certificate", error);
      Swal.fire(
        "Download failed",
        "We couldn't download the certificate. Please try again.",
        "error"
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const getRetakePath = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const teacherId = user?._id || user?.id || "";
    
    if (userRole === 3) {
      // Individual teacher
      return "/test/mcq-test";
    } else if (userRole === 2) {
      // School teacher
      return teacherId
        ? `/teachertest/mcq-test?teacher_id=${encodeURIComponent(teacherId)}`
        : "/teachertest/mcq-test";
    } else {
      // School admin (role 1) - can also take courses
      return "/test/mcq-test";
    }
  };

  return (
    <DashboardLayout 
      role={userRole} 
      userType={userType}
      activeMenu="Certifications"
      layoutType="topnav"
    >
      <div className="card">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between flex-wrap mb-4">
            <div>
              <h5 className="mb-1">My Certifications Overview</h5>
              <p className="text-muted mb-0">
                View all your completed certifications and training courses.
              </p>
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : certifications.length === 0 ? (
            <div className="alert alert-info">
              <p className="mb-0">
                You don&apos;t have any certifications yet. Complete training courses to earn certificates.
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped align-middle">
                <thead>
                  <tr>
                    <th>Course</th>
                    <th>Status</th>
                    <th>Completed</th>
                    <th>Renew by</th>
                    <th>Hours</th>
                    <th>Certificate</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {certifications.map((cert) => {
                    const label = getCourseLabel(cert);
                    const statusLabel =
                      statusLabels[cert.status] || cert.status || "Not Started";
                    const isCompleted = ["completed", "approved"].includes(
                      (cert.status || "").toLowerCase()
                    );
                    const retakePath = getRetakePath();
                    
                    return (
                      <tr key={`${cert.courseKey}-${cert._id || "record"}`}>
                        <td>
                          <div className="d-flex align-items-center">
                            {isCompleted && (
                              <i
                                className="bx bx-check-circle text-success me-2"
                                style={{ fontSize: "1.2rem" }}
                                title="Certified"
                              />
                            )}
                            {label}
                          </div>
                        </td>
                        <td>
                          <span className="badge bg-light text-dark">
                            {statusLabel}
                          </span>
                        </td>
                        <td>
                          {cert.dateCompleted
                            ? formatDateDisplay(cert.dateCompleted)
                            : "—"}
                        </td>
                        <td>
                          {cert.renewByDate
                            ? formatDateDisplay(cert.renewByDate)
                            : "—"}
                        </td>
                        <td>
                          {cert.hours !== undefined && cert.hours !== null
                            ? cert.hours
                            : "—"}
                        </td>
                        <td>
                          {/*
                            A stored certificate file if there is one, otherwise
                            the certificate rendered on demand from the record.
                            This used to offer only the first, and
                            certificateUrl is null on every completed
                            certification in the platform — so this column
                            showed an em dash for everybody while the dashboard
                            offered a working link for the same row.
                          */}
                          {cert.certificateUrl ? (
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0"
                              onClick={() =>
                                handleDownloadCertificate(cert, label)
                              }
                              disabled={
                                downloadingId ===
                                getCertificateIdentifier(cert)
                              }
                            >
                              {downloadingId ===
                              getCertificateIdentifier(cert)
                                ? "Downloading..."
                                : "View"}
                            </button>
                          ) : certificateLinkFor(cert, fallbackUserId) ? (
                            <Link
                              to={certificateLinkFor(cert, fallbackUserId)}
                              className="btn btn-link btn-sm p-0"
                            >
                              View
                            </Link>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          <Link
                            to={retakePath}
                            className="btn btn-sm btn-outline-primary"
                          >
                            <i className="bx bx-refresh me-1"></i>
                            Retake Course
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CompletedCertifications;

